import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/v5/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/v5/components/ui/accordion";
import { Input } from "@/v5/components/ui/input";
import {
  fetchSmsTemplateList,
  fetchSmsTemplate,
  fetchSmsMostUsed,
  resetSmsMostUsed,
  type SmsCategory,
  type MostUsedTemplate,
} from "@/v5/hooks/useSmsTemplates";
import { Loader2, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

interface SmsTemplatePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (message: string) => void;
  patientId?: string;
  planId?: string;
}

export function SmsTemplatePicker({
  open,
  onOpenChange,
  onApply,
  patientId,
  planId,
}: SmsTemplatePickerProps) {
  const [categoryList, setCategoryList] = useState<SmsCategory[]>([]);
  const [mostUsed, setMostUsed] = useState<MostUsedTemplate[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState<string | null>(null);

  const q = query.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!q) return [];
    return categoryList
      .map((cat) => ({
        ...cat,
        templates: cat.templates.filter((t) =>
          t.name.toLowerCase().includes(q),
        ),
      }))
      .filter((cat) => cat.templates.length > 0);
  }, [categoryList, q]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchSmsTemplateList()
      .then(setCategoryList)
      .catch(() => setCategoryList([]))
      .finally(() => setLoading(false));
    fetchSmsMostUsed()
      .then(setMostUsed)
      .catch(() => setMostUsed([]));
  }, [open]);

  const handleClose = useCallback(
    (v: boolean) => {
      if (!v) {
        setCategoryList([]);
        setMostUsed([]);
        setQuery("");
        setApplying(null);
      }
      onOpenChange(v);
    },
    [onOpenChange],
  );

  const handleReset = useCallback(async (id: string) => {
    setMostUsed((prev) => prev.filter((t) => t.id !== id));
    try {
      await resetSmsMostUsed(id);
    } catch {
      /* optimistic; will reappear on next open if it failed */
    }
  }, []);

  const handleSelect = useCallback(
    async (id: string) => {
      setApplying(id);
      try {
        const detail = await fetchSmsTemplate(id, patientId, planId);
        onApply(detail.message);
        handleClose(false);
      } catch {
        /* let user retry */
      } finally {
        setApplying(null);
      }
    },
    [onApply, handleClose, patientId, planId],
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>SMS Templates</DialogTitle>
          <DialogDescription>
            Select a template to insert into your message
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search templates..."
            className="pl-9"
          />
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && q && (
          <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6">
            {searchResults.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No templates match "{query.trim()}"
              </p>
            ) : (
              <div className="space-y-3 pb-2">
                {searchResults.map((cat) => (
                  <div key={cat.id}>
                    <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground py-2">
                      {cat.name}
                    </p>
                    <div className="space-y-1">
                      {cat.templates.map((tpl) => (
                        <button
                          key={tpl.id}
                          disabled={applying !== null}
                          onClick={() => handleSelect(tpl.id)}
                          className="w-full text-left rounded-lg bg-muted/40 px-4 py-2.5 text-sm transition-colors hover:bg-primary hover:text-white disabled:opacity-60 flex items-center justify-between"
                        >
                          <span>{tpl.name}</span>
                          {applying === tpl.id && (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!loading && !q && (
          <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6">
            {mostUsed.length > 0 && (
              <div className="pb-3 mb-2 border-b border-border">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground py-2">
                  Most Used
                </p>
                <div className="space-y-1">
                  {mostUsed.map((tpl) => (
                    <div
                      key={tpl.id}
                      className="group w-full rounded-lg bg-muted/40 text-sm transition-colors hover:bg-primary hover:text-white flex items-center"
                    >
                      <button
                        disabled={applying !== null}
                        onClick={() => handleSelect(tpl.id)}
                        className="flex-1 text-left px-4 py-2.5 disabled:opacity-60 flex items-center justify-between min-w-0"
                      >
                        <span className="truncate">{tpl.name}</span>
                        {applying === tpl.id && (
                          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                        )}
                      </button>
                      <button
                        type="button"
                        aria-label={`Remove ${tpl.name} from most used`}
                        title="Remove from Most Used"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReset(tpl.id);
                        }}
                        className="px-3 py-2.5 text-muted-foreground group-hover:text-white/80 hover:!text-white"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {categoryList.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No SMS templates available
              </p>
            ) : (
              <Accordion type="multiple" className="pb-2">
                {categoryList.map((cat) => (
                  <AccordionItem key={cat.id} value={cat.id}>
                    <AccordionTrigger className="text-xs font-bold uppercase tracking-wide text-muted-foreground hover:no-underline">
                      {cat.name}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-1">
                        {cat.templates.map((tpl) => (
                          <button
                            key={tpl.id}
                            disabled={applying !== null}
                            onClick={() => handleSelect(tpl.id)}
                            className="w-full text-left rounded-lg bg-muted/40 px-4 py-2.5 text-sm transition-colors hover:bg-primary hover:text-white disabled:opacity-60 flex items-center justify-between"
                          >
                            <span>{tpl.name}</span>
                            {applying === tpl.id && (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            )}
                          </button>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
